import React, { useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { FileText, Clock, Play, CheckCircle, XCircle, RefreshCw, Eye } from 'lucide-react';
import { useApiData } from '../../hooks/useApi';
import apiService from '../../services/api';

const ReferralPartnerDashboard = ({ user, onViewApplications }) => {
  const [timeRange, setTimeRange] = useState('7d');
  const [refreshing, setRefreshing] = useState(false);

  const {
    data: dashboardData,
    loading: statsLoading,
    refetch,
  } = useApiData(
    () => apiService.getReferralPartnerStats(user?.id, timeRange),
    [timeRange, user?.id]
  );

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await refetch();
    } finally {
      setRefreshing(false);
    }
  };

  const stats = useMemo(
    () => [
      {
        icon: FileText,
        label: 'Total Applications Submitted',
        value: dashboardData?.totalApplications || '0',
        color: 'var(--primary)',
        description: 'All applications via your referral link',
      },
      {
        icon: Clock,
        label: 'Pending Applications',
        value: dashboardData?.pendingApplications || '0',
        color: 'var(--warning)',
        description: 'Awaiting initial review',
      },
      {
        icon: Play,
        label: 'Applications Under Process',
        value: dashboardData?.processingApplications || '0',
        color: 'var(--info)',
        description: 'Currently being processed',
      },
      {
        icon: CheckCircle,
        label: 'Total Registrations',
        value: dashboardData?.totalRegistrations || '0',
        color: 'var(--success)',
        description: 'Successfully registered students',
      },
      {
        icon: XCircle,
        label: 'Total Applications Rejected',
        value: dashboardData?.rejectedApplications || '0',
        color: 'var(--danger)',
        description: 'Applications that were rejected',
      },
    ],
    [dashboardData]
  );

  if (statsLoading) {
    return (
      <div className='responsive-card'>
        <div className='responsive-loading'>
          <div className='responsive-spinner' />
          <p>Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <motion.div
        className='responsive-card'
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className='responsive-card-header'>
          <div>
            <h2
              style={{
                fontSize: 'var(--text-2xl)',
                fontWeight: '700',
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                marginBottom: 'var(--space-2)',
              }}
            >
              Welcome back, {user?.name}!
            </h2>
            <p style={{ color: 'var(--gray-600)', fontSize: 'var(--text-sm)' }}>
              Track your referral applications and performance.
            </p>
          </div>

          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
            <select
              className='responsive-form-input responsive-form-select'
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              style={{ minWidth: '120px' }}
            >
              <option value='1d'>Last 24h</option>
              <option value='7d'>Last 7 days</option>
              <option value='30d'>Last 30 days</option>
              <option value='90d'>Last 90 days</option>
            </select>

            <motion.button
              className='responsive-btn responsive-btn-secondary responsive-btn-sm'
              onClick={handleRefresh}
              disabled={refreshing}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <motion.div
                animate={{ rotate: refreshing ? 360 : 0 }}
                transition={{ duration: 1, repeat: refreshing ? Infinity : 0, ease: 'linear' }}
              >
                <RefreshCw size={16} />
              </motion.div>
            </motion.button>
          </div>
        </div>
      </motion.div>

      <div className='responsive-grid cols-3'>
        {stats.map((stat, index) => {
          const IconComponent = stat.icon;

          return (
            <motion.div
              key={index}
              className='responsive-card'
              initial={{ opacity: 0, y: 30, scale: 0.9 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ delay: index * 0.1, duration: 0.6, type: 'spring' }}
              whileHover={{ y: -4, scale: 1.02 }}
            >
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 'var(--space-4)' }}>
                <motion.div
                  style={{
                    width: '60px',
                    height: '60px',
                    borderRadius: 'var(--radius-xl)',
                    background: `linear-gradient(135deg, ${stat.color}15 0%, ${stat.color}25 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                  whileHover={{ rotate: 10, scale: 1.1 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <IconComponent size={24} style={{ color: stat.color }} />
                </motion.div>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <motion.h3
                    style={{
                      fontSize: 'var(--text-2xl)',
                      fontWeight: '800',
                      color: 'var(--gray-900)',
                      marginBottom: 'var(--space-1)',
                      lineHeight: 1,
                    }}
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ delay: 0.3 + index * 0.1, type: 'spring' }}
                  >
                    {stat.value}
                  </motion.h3>

                  <p
                    style={{
                      fontSize: 'var(--text-sm)',
                      fontWeight: '600',
                      color: 'var(--gray-700)',
                      marginBottom: 'var(--space-2)',
                    }}
                  >
                    {stat.label}
                  </p>

                  <p
                    style={{
                      fontSize: 'var(--text-xs)',
                      color: 'var(--gray-500)',
                    }}
                  >
                    {stat.description}
                  </p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>

      <motion.div
        className='responsive-card'
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.6 }}
      >
        <div style={{ padding: 'var(--space-4)' }}>
          <motion.button
            className='responsive-btn responsive-btn-primary'
            onClick={() => onViewApplications?.(user?.id)}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--space-2)',
              width: 'fit-content',
            }}
          >
            <Eye size={18} />
            VIEW APPLICATIONS
          </motion.button>
        </div>
      </motion.div>
    </div>
  );
};

export default ReferralPartnerDashboard;
